import { useEffect, useRef } from 'react';
import { useHorizontalScrolling } from '@atlassian/jira-portfolio-3-horizontal-scrolling';
import { useDndScrolling } from '../utils';

type Props = {
	scrollElement: Element;
	leftSidebarWidth: number;
};

const SCROLL_SCALE = 10;
const getWeightedScrollAmount = (scrollAmount: number): number => scrollAmount ** SCROLL_SCALE;

const SCROLL_MAX = 150;
const getThrottledScrollAmount = (scrollAmount: number): number =>
	Math.min(SCROLL_MAX, scrollAmount);

const SCROLL_THRESHOLD = 50;

const getScrollPosDelta = (height: number, cursorOffset: number): number => {
	const threshold = Math.min(height / 2, SCROLL_THRESHOLD);
	if (cursorOffset >= 0 && cursorOffset <= threshold) {
		// inversely relative to distance to list top
		const distanceFromTopToCursor = cursorOffset;
		const scrollAmount = getWeightedScrollAmount(1 / (distanceFromTopToCursor / threshold));
		return -getThrottledScrollAmount(scrollAmount);
	}

	if (cursorOffset <= height && cursorOffset >= height - threshold) {
		// inversely relative to distance to list bottom
		const distanceToBottomFromCursor = height - cursorOffset;
		const scrollAmount = getWeightedScrollAmount(1 / (distanceToBottomFromCursor / threshold));
		return getThrottledScrollAmount(scrollAmount);
	}

	return 0;
};

export default function DndScrollingObserver({ scrollElement, leftSidebarWidth }: Props) {
	const [{ dragging }] = useDndScrolling();
	const [{ viewport, container, zoomLevel }, { scrollTo, setSmooth }] = useHorizontalScrolling();

	// Need to change viewport offset into px
	const scrollLeftRef = useRef(0);
	const offsetPx = (viewport.offset * container.width) / container.duration;

	useEffect(() => {
		scrollLeftRef.current = offsetPx;
	}, [offsetPx]);

	useEffect(() => {
		if (!dragging) {
			return;
		}

		const minTop = 0;
		const maxTop = scrollElement.scrollHeight - scrollElement.clientHeight;

		let scrollTop = scrollElement.scrollTop;
		let scrollLeft = scrollLeftRef.current;
		let verticalDelta = 0;
		let horizontalDelta = 0;
		let stopped = false;
		let timeoutId: ReturnType<typeof setTimeout>;

		const scroll = () => {
			if (verticalDelta !== 0) {
				scrollTop += verticalDelta;
				scrollTop = Math.max(Math.min(scrollTop, maxTop), minTop);
				scrollElement.scrollTo(0, scrollTop);
			}

			// is zoomLevel is undefined, we don't want horizontal scrolling enabled
			if (horizontalDelta !== 0 && zoomLevel) {
				scrollLeft += horizontalDelta;
				setSmooth(true);
				scrollTo(scrollLeft, 'dragndrop');
			}

			if (!stopped) {
				timeoutId = setTimeout(scroll, 333);
			}
		};

		scroll();

		const { top, height, width } = scrollElement.getBoundingClientRect();

		const handleMouseMove = (e: MouseEvent) => {
			// Vertical
			const cursorOffset = e.clientY - top;
			verticalDelta = getScrollPosDelta(height, cursorOffset);

			// Horizontal
			const left = width - viewport.width + leftSidebarWidth;
			const horizontalCursorOffset = e.clientX - left;
			horizontalDelta = getScrollPosDelta(viewport.width, horizontalCursorOffset);
		};

		stopped = false;
		document.addEventListener('mousemove', handleMouseMove);

		return () => {
			document.removeEventListener('mousemove', handleMouseMove);
			stopped = true;
			clearTimeout(timeoutId);
			setSmooth(false);
		};
	}, [viewport.width, dragging, scrollElement, scrollTo, setSmooth, zoomLevel, leftSidebarWidth]);
	return null;
}
